import { Controller } from "@hotwired/stimulus"

const COMPANY_FIELD = "task[contact_company_ids][]"
const OPPORTUNITY_FIELD = "task[opportunity_ids][]"
const SOURCE_FIELDS = ["task[lead_source_category_id]", "task[lead_source_id]"]
const SOURCE_FRAME = "task_source_fields"

// Never put these in the reload URL (a GET): the CSRF token would leak into
// URLs/logs, and free text can blow Puma's 10 KB query-string cap ("Content
// missing"). None of them feeds the frames this controller replaces — the
// Title/Description live outside them, and the title autofill state lives on
// the outer element, which a reload never touches.
const RELOAD_EXCLUDED = new Set([
  "authenticity_token",
  "_method",
  "task[name]",
  "task[description]",
])

/**
 * Server-driven dependent fields for the shared task form
 * (pipeline/tasks/_form_fields), opt-in per surface (CONEK-7690 — the calendar
 * task drawer's React TaskInformation parity). Unlike ui--dependent-frame, which
 * forwards ONE param, the Site-dependent rules need the whole form: which Site,
 * which Opportunity/Contact are picked (and so whether they belong to the new
 * Site), the Type (Appointment → Site owner as Assignee), the Status (Done →
 * Result). So this serializes the enclosing form, adds WHY it is reloading, and
 * points the relevant frames at `url`; Turbo extracts each frame from the
 * re-rendered form.
 *
 *   company()             Site changed           → task_company_dependent_fields
 *   type()                Type changed           → dependent fields + result
 *   status()              Status changed         → task_result_field
 *   toggleContacts()      contact toggle         → task_company_dependent_fields
 *   toggleOpportunities() opportunity toggle     → task_company_dependent_fields
 *   opportunity()         Opportunity changed    → task_source_fields
 *   source()              the rep picked Source  → (no reload) marks it theirs
 *
 * A Site change and the opportunity toggle may drop the Opportunity, so they
 * also replace task_source_fields (when the surface opts into the Source
 * inheritance — React CONEK-7179); the server re-infers the Source from the
 * Opportunity unless `source_user_set` says the rep chose it.
 *
 * `previous_company_id` lets the server apply React's "first Site switches the
 * recommendations on" rule; `reload_reason` lets it clear an out-of-Site pick
 * only where React did (a Site change or switching a toggle ON).
 */
export default class extends Controller {
  static targets = ["frame", "sourceUserSet", "sourceHint"]
  static values = { url: String }

  connect() {
    this.companyId = this._currentCompany()
  }

  company(event) {
    const field = event.target
    if (!field || field.name !== COMPANY_FIELD || field.disabled) return
    const previous = this.companyId
    const current = this._currentCompany()
    if (previous === current) return
    this.companyId = current
    this._reload(
      ["task_company_dependent_fields", SOURCE_FRAME],
      "company",
      previous,
    )
  }

  opportunity(event) {
    const field = event.target
    if (!field || field.name !== OPPORTUNITY_FIELD) return
    this._reload([SOURCE_FRAME], "opportunity")
  }

  // React SourceSection's single writer: any Source gesture makes the Source
  // the rep's (a clear hands it back to the Opportunity inference).
  source(event) {
    const changed = event.target?.name
    if (!SOURCE_FIELDS.includes(changed)) return
    // Clearing the category clears its subcategory too, but that frame is
    // re-fetched asynchronously (ui--dependent-frame), so it still holds the
    // old subcategory right now — don't let it count as a pick.
    const [categoryField] = SOURCE_FIELDS
    const categoryCleared = changed === categoryField && !event.target.value
    const picked =
      !categoryCleared &&
      SOURCE_FIELDS.some((name) => {
        const field = this.element.querySelector(`[name="${name}"]`)
        return field && field.value
      })
    if (this.hasSourceUserSetTarget)
      this.sourceUserSetTarget.value = picked ? "1" : "0"
    this.sourceHintTargets.forEach((hint) => hint.remove())
  }

  type() {
    this._reload(["task_company_dependent_fields", "task_result_field"], "type")
  }

  status() {
    this._reload(["task_result_field"], "status")
  }

  toggleContacts() {
    this._reload(["task_company_dependent_fields"], "toggle_contacts")
  }

  toggleOpportunities() {
    this._reload(
      ["task_company_dependent_fields", SOURCE_FRAME],
      "toggle_opportunities",
    )
  }

  _currentCompany() {
    const field = Array.from(
      this.element.querySelectorAll(`[name="${COMPANY_FIELD}"]`),
    ).find((input) => !input.disabled)
    return field ? field.value : ""
  }

  _reload(frameIds, reason, previous = this.companyId) {
    if (!this.urlValue) return
    const form = this.element.closest("form")
    const url = new URL(this.urlValue, window.location.origin)
    if (form) {
      for (const [key, value] of new FormData(form).entries()) {
        if (typeof value === "string" && !RELOAD_EXCLUDED.has(key))
          url.searchParams.append(key, value)
      }
    }
    url.searchParams.set("reload_reason", reason)
    url.searchParams.set("previous_company_id", previous || "")
    const src = url.pathname + url.search

    this.frameTargets
      .filter((frame) => frameIds.includes(frame.id))
      .forEach((frame) => frame.setAttribute("src", src))
  }
}
